import {
  Alert,
  Box,
  Card,
  CardContent,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { useState, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { DataTable } from '../../components/DataTable';
import { money } from '../../lib/format';
import { useShop } from '../../shop/ShopProvider';
import { DateRangeControl } from '../insights/components/DateRangeControl';
import { detectPreset, presetRange, todayIn, type DateRange } from '../insights/lib/dateRange';
import { FOODPANDA_PINK, useFoodpandaProfit, useFoodpandaSummary } from './api';

/** How Foodpanda did: orders, busy hours, best sellers, share of sales and profit after Foodpanda. */
export function ReportsTab() {
  const { format } = useShop();
  const { can } = useAuth();
  const today = todayIn(format.timeZone);
  const [range, setRange] = useState<DateRange>(presetRange('thisMonth', today));
  const financial = can('reports.financial');
  const summary = useFoodpandaSummary(range);
  const profit = useFoodpandaProfit(range, financial);

  if (!can('reports.view'))
    return <Alert severity="info">Foodpanda reports need the permission to view reports.</Alert>;
  const s = summary.data;
  const p = profit.data;
  return (
    <Stack spacing={2.5}>
      <Box>
        <DateRangeControl
          range={range}
          preset={detectPreset(range, today)}
          onPreset={(preset) => setRange(presetRange(preset, today))}
          onCustom={setRange}
        />
      </Box>
      {!s ? (
        <Skeleton variant="rounded" height={320} />
      ) : (
        <>
          <Grid>
            <Stat label="Orders" value={String(s.orders.total)} />
            <Stat label="Delivered" value={String(s.orders.completed)} />
            <Stat
              label="Rejected"
              value={`${s.orders.rejectRate}%`}
              hint={`${String(s.orders.rejected)} orders`}
              warn={Number(s.orders.rejectRate) >= 5}
            />
            <Stat
              label="Cancelled by Foodpanda"
              value={`${s.orders.cancelRate}%`}
              hint={`${String(s.orders.cancelled)} orders`}
            />
            <Stat
              label="Average preparation"
              value={
                s.orders.averagePrepMinutes === null
                  ? '—'
                  : `${String(s.orders.averagePrepMinutes)} min`
              }
            />
          </Grid>

          {financial && p && (
            <Panel title="Profit after Foodpanda">
              <Grid>
                <Stat label="Foodpanda sales" value={money(p.revenue, format)} />
                <Stat label="Cost of goods" value={money(p.cogs, format)} />
                <Stat label="Commission" value={money(p.commission, format)} />
                <Stat label="Fees" value={money(p.fees, format)} />
                <Stat
                  label="Profit"
                  value={money(p.profit, format)}
                  hint={`${p.marginPercent}% of sales`}
                  strong
                />
              </Grid>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
                Commission and fees come from payouts posted in this period ({p.payouts};{' '}
                {money(p.paidOut, format)} paid out). Foodpanda still owes {money(p.owed, format)}{' '}
                on its invoices.
              </Typography>
            </Panel>
          )}

          <Panel title="Sales by channel">
            <Stack spacing={1.5}>
              {s.channels.map((c) => (
                <Box key={c.channel}>
                  <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: c.channel === 'FOODPANDA' ? 700 : 400 }}
                    >
                      {c.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {money(c.net, format)} · {c.share}%
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, Number(c.share))}
                    aria-label={`${c.label} share of sales`}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      ...(c.channel === 'FOODPANDA'
                        ? { '& .MuiLinearProgress-bar': { bgcolor: FOODPANDA_PINK } }
                        : {}),
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </Panel>

          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,3fr) minmax(0,2fr)' },
            }}
          >
            <Panel title="Busy hours" hint="Foodpanda orders by the hour they came in">
              {s.orders.total === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No orders in this period.
                </Typography>
              ) : (
                <Box sx={{ mx: -1 }} data-testid="fp-peak-hours">
                  <BarChart
                    height={220}
                    margin={{ left: 0, right: 8, top: 8, bottom: 0 }}
                    borderRadius={3}
                    xAxis={[
                      {
                        scaleType: 'band',
                        data: s.peakHours.map((h) => h.hour),
                        valueFormatter: (v: number) => `${String(v).padStart(2, '0')}:00`,
                        tickInterval: (_v: number, i: number) => i % 3 === 0,
                        disableLine: true,
                        disableTicks: true,
                      },
                    ]}
                    yAxis={[{ tickMinStep: 1, disableLine: true, disableTicks: true, width: 28 }]}
                    series={[
                      {
                        data: s.peakHours.map((h) => h.orders),
                        label: 'Orders',
                        color: FOODPANDA_PINK,
                      },
                    ]}
                    hideLegend
                  />
                </Box>
              )}
            </Panel>
            <Panel title="Top items">
              <DataTable
                rows={s.topItems}
                rowKey={(i) => i.code ?? i.name}
                empty="No items sold in this period."
                columns={[
                  { key: 'name', header: 'Item', render: (i) => i.name },
                  {
                    key: 'qty',
                    header: 'Qty',
                    align: 'right',
                    render: (i) => String(Number(i.qty)),
                  },
                  {
                    key: 'rev',
                    header: 'Sales',
                    align: 'right',
                    render: (i) => money(i.revenue, format),
                  },
                ]}
              />
            </Panel>
          </Box>
        </>
      )}
    </Stack>
  );
}

function Grid({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', md: 'repeat(5, minmax(0,1fr))' },
      }}
    >
      {children}
    </Box>
  );
}

function Stat(props: {
  label: string;
  value: string;
  hint?: string;
  warn?: boolean;
  strong?: boolean;
}) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary">
          {props.label}
        </Typography>
        <Typography
          variant="h6"
          sx={{ fontWeight: props.strong ? 800 : 700 }}
          color={props.warn ? 'warning.main' : undefined}
        >
          {props.value}
        </Typography>
        {props.hint && (
          <Typography variant="caption" color="text.secondary">
            {props.hint}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function Panel({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        {hint && (
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        )}
        <Box sx={{ mt: 1.5 }}>{children}</Box>
      </CardContent>
    </Card>
  );
}
